import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Field } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { CloverLogo } from "../components/CloverLogo";

const ART = "https://static.prod-images.emergentagent.com/jobs/f758f835-44e9-4487-8a40-0fa1b663c2fc/images/2dc56ea3a888ced4c436ed33dabbb587a2222abf6dcc6f6577be07828b188d77.jpeg";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={loc.state?.from || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(loc.state?.from || "/", { replace: true });
    } catch (err) {
      setError(errMsg(err, "Gagal masuk. Periksa koneksi Anda."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[1.05fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#FFF5F3] lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-rose-100/70 blur-3xl" />
        <div className="absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-orange-100/70 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <CloverLogo className="h-10 w-10" testid="login-panel-logo" />
          <span className="font-heading text-lg font-semibold tracking-[0.08em] text-gray-900">CLOVER MANAGEMENT</span>
        </div>
        <img src={ART} alt="" className="relative mx-auto w-full max-w-md mix-blend-multiply [mask-image:radial-gradient(closest-side,black_62%,transparent)]" />
        <div className="relative max-w-md">
          <h2 className="font-heading text-3xl font-semibold leading-tight text-gray-900">Kelola voucher, kasbon & <span className="text-primary">gajian</span> jadi lebih mudah</h2>
          <p className="mt-3 text-[15px] leading-relaxed text-gray-600">Gajian setiap tanggal 15 dan akhir bulan — voucher, kasbon, dan pembayaran tercatat rapi dan konsisten otomatis.</p>
        </div>
      </div>

      <div className="flex items-center px-6 py-12 sm:px-12">
        <form onSubmit={submit} className="fade-up mx-auto w-full max-w-sm space-y-5 rounded-2xl border border-gray-100 bg-white p-6 shadow-[0_10px_40px_-16px_rgba(16,24,40,0.15)] sm:p-8" data-testid="login-form">
          <div>
            <div className="mb-5 flex items-center gap-3 lg:hidden">
              <CloverLogo className="h-11 w-11" testid="login-logo" />
              <p className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-gray-900">Clover Management</p>
            </div>
            <h1 className="font-heading text-[28px] font-semibold text-gray-900">Selamat datang</h1>
            <p className="mt-1 text-sm text-gray-500">Masuk sebagai admin untuk mengelola data gajian.</p>
          </div>
          <Field label="Email">
            <Input data-testid="login-email-input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-10 bg-white" placeholder="admin@gajian.id" />
          </Field>
          <Field label="Password">
            <Input data-testid="login-password-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-10 bg-white" />
          </Field>
          {error && <p data-testid="login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
          <Button data-testid="login-submit-button" type="submit" disabled={loading} className="h-10 w-full rounded-full text-base font-semibold active:scale-[0.98]">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Masuk <ArrowRight className="ml-1 h-4 w-4" /></>}
          </Button>
          <p className="text-center text-sm text-gray-500">
            Anak? <Link to="/portal/login" data-testid="login-to-portal" className="font-semibold text-primary hover:underline">Check-in Mandiri</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
